"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import { authApi } from "@/lib/api";
import { NavLinks } from "./NavLinks";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    authApi.me().then(({ data: user }) => {
      setIsLoggedIn(!!user);
    }).catch(() => setIsLoggedIn(false));
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        background: scrolled ? "rgba(10,9,8,0.95)" : "transparent",
        backdropFilter: scrolled ? "blur(20px)" : "none",
        WebkitBackdropFilter: scrolled ? "blur(20px)" : "none",
        borderBottom: scrolled ? "1px solid rgba(201,168,76,0.12)" : "1px solid transparent",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">

{/* Logo */}
        <Link href="/" className="flex items-center cursor-pointer group">
          {mobileOpen ? (
            <Image src="/squre-logo.png" alt="AureliusCo logo" width={72} height={72} className="h-[72px] w-auto" priority />
          ) : (
            <Image src="/logo.png" alt="AureliusCo logo" width={162} height={72} className="h-[72px] w-auto" priority />
          )}
        </Link>

          {/* Desktop Nav */}
          <NavLinks className="hidden md:flex items-center gap-8" />

          {/* Auth */}
          <div className="hidden md:flex items-center gap-5">
            {isLoggedIn === null ? (
              <div className="w-28 h-7 animate-pulse" style={{ background: "rgba(201,168,76,0.1)" }} />
            ) : isLoggedIn ? (
              <Link
                href="/dashboard"
                className="px-5 py-2 text-xs font-medium tracking-widest uppercase transition-all duration-300 cursor-pointer"
                style={{ background: "linear-gradient(135deg, #C9A84C, #E8D5A3)", color: "#0A0908", fontFamily: "'Montserrat', sans-serif" }}
              >
                Dashboard
              </Link>
            ) : (
              <>
                <Link
                  href="/login"
                  className="text-xs tracking-widest uppercase transition-colors duration-200 cursor-pointer"
                  style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "rgba(201,168,76,0.7)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "rgba(245,240,232,0.4)"; }}
                >
                  Sign In
                </Link>
                <Link
                  href="/login"
                  className="px-5 py-2 text-xs font-medium tracking-widest uppercase transition-all duration-300 cursor-pointer"
                  style={{ background: "linear-gradient(135deg, #C9A84C, #E8D5A3)", color: "#0A0908", fontFamily: "'Montserrat', sans-serif" }}
                >
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileOpen}
            className="md:hidden p-2 transition-colors duration-200 cursor-pointer"
            style={{ color: "rgba(201,168,76,0.7)" }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5} aria-hidden="true">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            style={{ background: "rgba(10,9,8,0.98)", borderBottom: "1px solid rgba(201,168,76,0.12)" }}
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              <NavLinks
                className="flex flex-col gap-4"
                onClick={() => setMobileOpen(false)}
              />
              <div className="mt-2 flex flex-col gap-3">
                {isLoggedIn ? (
                  <Link
                    href="/dashboard"
                    onClick={() => setMobileOpen(false)}
                    className="py-3 text-xs font-medium text-center tracking-widest uppercase cursor-pointer"
                    style={{ background: "linear-gradient(135deg, #C9A84C, #E8D5A3)", color: "#0A0908", fontFamily: "'Montserrat', sans-serif" }}
                  >
                    Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      href="/login"
                      onClick={() => setMobileOpen(false)}
                      className="py-3 text-xs text-center tracking-widest uppercase transition-colors duration-200 cursor-pointer"
                      style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}
                    >
                      Sign In
                    </Link>
                    <Link
                      href="/login"
                      onClick={() => setMobileOpen(false)}
                      className="py-3 text-xs font-medium text-center tracking-widest uppercase cursor-pointer"
                      style={{ background: "linear-gradient(135deg, #C9A84C, #E8D5A3)", color: "#0A0908", fontFamily: "'Montserrat', sans-serif" }}
                    >
                      Get Started
                    </Link>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
